import { useCallback } from 'react';
import { App } from 'antd';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import type { SKU } from '../types';

/**
 * SKU操作处理Hook
 * 封装导出、删除、重新计算等操作逻辑
 */
export const useSKUActions = (
  skuId: string | undefined,
  sku: SKU | null,
  periodsLength: number,
  onDataReload: () => void
) => {
  const { message } = App.useApp();
  const navigate = useNavigate();

  const handleExport = useCallback(async (format: 'excel' | 'pdf') => {
    if (periodsLength === 0) {
      message.warning('请先上传周期数据');
      return;
    }

    try {
      const response = await api.get(`/skus/${skuId}/export/`, {
        params: { format },
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${sku?.name}_all_periods.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      message.success(`${format.toUpperCase()}导出成功`);
    } catch (error) {
      message.error('导出失败');
      console.error(error);
    }
  }, [skuId, sku, periodsLength, message]);

  const handleExportEnhancedExcel = useCallback(async () => {
    try {
      message.loading({ content: '正在生成报告...', key: 'export', duration: 0 });
      const response = await api.get(`/skus/${skuId}/export_enhanced_excel/`, {
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${sku?.name}_market_analysis.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      message.success({ content: 'Excel报告导出成功', key: 'export' });
    } catch (error) {
      message.error({ content: '导出失败', key: 'export' });
      console.error(error);
    }
  }, [skuId, sku, message]);

  const handleDelete = useCallback(async () => {
    if (!window.confirm('确定要删除这个SKU吗？')) return;

    try {
      await api.delete(`/skus/${skuId}/`);
      message.success('删除成功');
      navigate('/aba-analysis');
    } catch (error) {
      message.error('删除失败');
      console.error(error);
    }
  }, [skuId, navigate, message]);

  return {
    handleExport,
    handleExportEnhancedExcel,
    handleDelete,
  };
};

